import { setStartChatWithPhoneNumberOpen } from "@common-reducers/MainReducer";
import { selectDefaultPhoneCountry } from "@common-reducers/UserSelectors";
import { setInvalidWANumber } from "@common-reducers/WhatsAppReducer";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/common/components/dialog";
import { openPhoneChatThunk } from "@extension/context/content/thunks/WhatsappThunks";
import { changeDefaultChatCountryCode } from '@common-reducers/UserThunks';
import { parsePhoneNumber } from 'libphonenumber-js';
import { usePhoneInput, defaultCountries, parseCountry, CountryData } from 'react-international-phone';
import { MessageCircle, Globe, Search } from 'lucide-react';
import ExtensionFlagImage from '@common-components/extension-flag-image/extension-flag-image';
import React, { useCallback, useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { createCustomCountries } from './countryUtils';



// Chat ID pattern: digits@c.us, digits-digits@g.us, digits@newsletter
const CHAT_ID_REGEX = /^[\d]+([-][\d]+)?@(c\.us|g\.us|newsletter|s\.whatsapp\.net)$/;

function isChatIdInput(value: string): boolean {
    return value.includes('@');
}

export interface StartChatWithPhoneNumberState {
    phone?: string;
    validPhone?: boolean;
}

export const StartChatWithPhoneNumber = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(namespace);
    const defaultCountry = useAppSelector(selectDefaultPhoneCountry);
    const isOpen = useAppSelector((state) => state.MainReducer.startChatWithPhoneNumberOpen);
    const invalidWANumber = useAppSelector((state) => state.WhatsAppReducer.invalidWANumber);

    const [phoneNumber, setPhoneNumber] = useState('');
    const [validPhone, setValidPhone] = useState(false);
    const [isCountryPickerOpen, setIsCountryPickerOpen] = useState(false);
    const [countrySearchQuery, setCountrySearchQuery] = useState('');
    const countrySearchInputRef = React.useRef<HTMLInputElement>(null);
    const [chatIdInput, setChatIdInput] = useState('');
    const [inputMode, setInputMode] = useState<'phone' | 'chatId'>('phone');
    
    // יצירת רשימת מדינות מותאמת אישית עם מסכה ארוכה יותר לארגנטינה
    const customCountries = useMemo(() => {
        return createCustomCountries(defaultCountries);
    }, []);
    
    const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
        defaultCountry: defaultCountry || 'us',
        value: phoneNumber,
        countries: customCountries,
        defaultMask: "...............", // 15 תווים
        onChange: (data) => {
            const { isValid, formattedPhoneNumber } = validatePhone(data.phone);
            const resPhone = formattedPhoneNumber != null ? formattedPhoneNumber : data.phone;
            setPhoneNumber(resPhone);
            setValidPhone(isValid);
            dispatch(setInvalidWANumber(''));
            dispatch(changeDefaultChatCountryCode({ defaultChatCountryCode: data.country.iso2 }));
        },
    });

    const validatePhone = (phone: string): { isValid: boolean, formattedPhoneNumber: string | null } => {
        let isValid = false;
        let formattedPhoneNumber: string | null = null;
        try {
            const parsedPhoneNumber = parsePhoneNumber(phone);
            isValid = parsedPhoneNumber.isPossible();
            formattedPhoneNumber = parsedPhoneNumber.number;
        } catch (error) {
            // Invalid phone number
        }
        return { isValid, formattedPhoneNumber };
    };


    const handleClose = useCallback(() => {
        dispatch(setStartChatWithPhoneNumberOpen(false));
        dispatch(setInvalidWANumber(''));
    }, [dispatch]);

    // Reset local state whenever the dialog is closed (from any source: thunk, dialog overlay, etc.)
    React.useEffect(() => {
        if (!isOpen) {
            setPhoneNumber('');
            setValidPhone(false);
            setIsCountryPickerOpen(false);
            setCountrySearchQuery('');
            setChatIdInput('');
            setInputMode('phone');
        }
    }, [isOpen]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            if (inputMode === 'chatId') {
                handleStartChat();
            } else {
                dispatch(openPhoneChatThunk({ phoneNumber }));
            }
        }
    };

    const handleChatIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setChatIdInput(value);
        dispatch(setInvalidWANumber(''));
    };

    const handleStartChat = () => {
        if (inputMode === 'chatId') {
            if (chatIdInput.trim()) {
                dispatch(openPhoneChatThunk({ phoneNumber: chatIdInput.trim() }));
            }
        } else {
            dispatch(openPhoneChatThunk({ phoneNumber }));
        }
    };

    const isStartChatDisabled = inputMode === 'chatId'
        ? !chatIdInput.trim()
        : !phoneNumber || !phoneNumber.startsWith('+');

    // Filter countries based on search query
    const filteredCountries = React.useMemo(() => {
        if (!countrySearchQuery.trim()) {
            return customCountries;
        }
        
        const query = countrySearchQuery.toLowerCase();
        return customCountries.filter((countryData: CountryData) => {
            const parsed = parseCountry(countryData);
            return (
                parsed.name.toLowerCase().includes(query) ||
                parsed.dialCode.includes(query) ||
                parsed.iso2.toLowerCase().includes(query)
            );
        });
    }, [countrySearchQuery, customCountries]);

    // Handle country selection
    const handleCountrySelect = (countryData: CountryData) => {
        const parsed = parseCountry(countryData);
        setCountry(parsed.iso2);
        // Set the phone input to start with the country dial code
        const newValue = `+${parsed.dialCode}`;
        const syntheticEvent = {
            target: { value: newValue },
            currentTarget: { value: newValue },
            preventDefault: () => {},
            stopPropagation: () => {},
            nativeEvent: {} as Event,
        } as React.ChangeEvent<HTMLInputElement>;
        handlePhoneValueChange(syntheticEvent);
        setIsCountryPickerOpen(false);
        setCountrySearchQuery('');
        // Focus back on phone input
        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
    };

    // Auto-focus search input when dropdown opens
    React.useEffect(() => {
        if (isCountryPickerOpen && countrySearchInputRef.current) {
            setTimeout(() => {
                countrySearchInputRef.current?.focus();
            }, 100);
        }
    }, [isCountryPickerOpen]);

    // Handle clicks on the form container
    const handleFormClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        const dropdown = target.closest('[data-country-picker]');
        const globeButton = target.closest('[data-globe-button]');
        const countryButton = target.closest('[data-country-button]');
        
        // Close dropdown if clicking outside of it (but not on globe button)
        if (isCountryPickerOpen && !dropdown && !globeButton && !countryButton) {
            setIsCountryPickerOpen(false);
            setCountrySearchQuery('');
        }
    };

    // Handle keypress on the form container
    const handleFormKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape' && isCountryPickerOpen) {
            setIsCountryPickerOpen(false);
            setCountrySearchQuery('');
            inputRef.current?.focus();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="max-w-md w-full p-0 border-0">
                <div
                    className="p-6 space-y-6"
                    style={{ backgroundColor: 'var(--color-surface, #ffffff)' }}
                    onClick={handleFormClick}
                    onKeyDown={handleFormKeyDown}
                >
                    <DialogHeader className="space-y-1 pr-8">
                        <DialogTitle className="text-xl font-semibold tracking-tight" style={{ color: 'var(--color-text, #111827)' }}>
                            {t('startChatWithPhoneNumber.title')}
                        </DialogTitle>
                        <p className="text-sm" style={{ color: 'var(--color-text-muted, #6b7280)' }}>
                            {inputMode === 'phone'
                                ? t('startChatWithPhoneNumber.subtitle')
                                : t('startChatWithPhoneNumber.chatIdSubtitle', 'Enter a WhatsApp chat ID (e.g. 1234567890@c.us)')}
                        </p>
                    </DialogHeader>

                    {/* Mode Toggle Tabs */}
                    <div className="flex rounded-lg border overflow-hidden" style={{ borderColor: 'var(--color-border, #e5e7eb)' }}>
                        <button
                            type="button"
                            onClick={() => setInputMode('phone')}
                            className="flex-1 px-3 py-2 text-sm font-medium transition-colors"
                            style={{
                                backgroundColor: inputMode === 'phone' ? 'var(--color-primary, #3b82f6)' : 'transparent',
                                color: inputMode === 'phone' ? 'var(--color-primary-contrast, #ffffff)' : 'var(--color-text-muted, #6b7280)',
                            }}
                        >
                            {t('startChatWithPhoneNumber.phoneNumber', 'Phone Number')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setInputMode('chatId')}
                            className="flex-1 px-3 py-2 text-sm font-medium transition-colors"
                            style={{
                                backgroundColor: inputMode === 'chatId' ? 'var(--color-primary, #3b82f6)' : 'transparent',
                                color: inputMode === 'chatId' ? 'var(--color-primary-contrast, #ffffff)' : 'var(--color-text-muted, #6b7280)',
                            }}
                        >
                            {t('startChatWithPhoneNumber.chatId', 'Chat ID')}
                        </button>
                    </div>

                    {inputMode === 'phone' ? (
                    /* Phone Input */
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <label htmlFor="phone" className="text-sm font-medium leading-none" style={{ color: 'var(--color-text, #111827)' }}>
                                {t('startChatWithPhoneNumber.phoneNumber')}
                            </label>

                            {/* Country Flag Badge - Outside input, next to label */}
                            {country && inputValue && inputValue.startsWith(`+${country.dialCode}`) && (() => {
                                const countryData = customCountries.find((c: CountryData) => {
                                    const parsed = parseCountry(c);
                                    return parsed.iso2 === country.iso2;
                                });
                                const parsedCountry = countryData ? parseCountry(countryData) : null;
                                return parsedCountry ? (
                                    <div
                                        className="flex items-center gap-2 rounded-lg px-2.5 py-1 border"
                                        style={{
                                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                            borderColor: 'rgba(59, 130, 246, 0.2)',
                                            color: 'var(--color-primary, #3b82f6)'
                                        }}
                                    >
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            <ExtensionFlagImage
                                                iso2={country.iso2}
                                                size="16px"
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    display: 'block',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </div>
                                        <span className="text-xs font-semibold">{country.dialCode}</span>
                                        <span className="text-xs" style={{ color: 'var(--color-text-muted, #6b7280)' }}>•</span>
                                        <span className="text-xs font-medium">{parsedCountry.name}</span>
                                    </div>
                                ) : null;
                            })()}
                        </div>

                        {/* Phone Number Input with Country Picker */}
                        <div className="relative">
                            <input
                                id="phone"
                                ref={inputRef}
                                type="tel"
                                placeholder="+1 234 567 8900"
                                value={inputValue}
                                onChange={handlePhoneValueChange}
                                onKeyDown={handleKeyDown}
                                className={cn(
                                    "flex h-11 w-full rounded-lg border bg-background pl-3 pr-10 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all"
                                )}
                                style={{
                                    borderColor: 'var(--color-border, #e5e7eb)',
                                    backgroundColor: 'var(--color-surface, #ffffff)',
                                    color: 'var(--color-text, #111827)',
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                                    e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = 'var(--color-border, #e5e7eb)';
                                    e.target.style.boxShadow = '';
                                }}
                            />

                            {/* Globe Icon Button */}
                            <button
                                type="button"
                                data-globe-button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsCountryPickerOpen(!isCountryPickerOpen);
                                }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm opacity-70 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 p-1"
                                style={{
                                    color: 'var(--color-text-muted, #6b7280)',
                                }}
                            >
                                <Globe className="h-4 w-4" />
                                <span className="sr-only">{t('startChatWithPhoneNumber.selectCountry')}</span>
                            </button>

                            {/* Country Picker Dropdown */}
                            {isCountryPickerOpen && (
                                <div
                                    data-country-picker
                                    className="absolute top-full left-0 right-0 mt-2 rounded-lg border shadow-lg z-50 animate-in fade-in-0 zoom-in-95 duration-200"
                                    style={{
                                        backgroundColor: 'var(--color-surface, #ffffff)',
                                        borderColor: 'var(--color-border, #e5e7eb)',
                                        maxHeight: '240px',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    {/* Search Bar */}
                                    <div className="p-2 border-b" style={{ borderColor: 'var(--color-border, #e5e7eb)' }}>
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'var(--color-text-muted, #6b7280)' }} />
                                            <input
                                                ref={countrySearchInputRef}
                                                type="text"
                                                placeholder={t('startChatWithPhoneNumber.searchCountries')}
                                                value={countrySearchQuery}
                                                onChange={(e) => setCountrySearchQuery(e.target.value)}
                                                className="w-full h-9 rounded-md border bg-background pl-9 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                                style={{
                                                    borderColor: 'var(--color-border, #e5e7eb)',
                                                    backgroundColor: 'var(--color-surface, #ffffff)',
                                                    color: 'var(--color-text, #111827)',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Country List */}
                                    <div className="overflow-y-auto" style={{ maxHeight: '200px' }}>
                                        {filteredCountries.length > 0 ? (
                                            filteredCountries.map((countryData: CountryData) => {
                                                const parsed = parseCountry(countryData);
                                                return (
                                                    <button
                                                        key={parsed.iso2}
                                                        type="button"
                                                        data-country-button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCountrySelect(countryData);
                                                        }}
                                                        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted transition-colors"
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'var(--color-muted, #f3f4f6)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                        }}
                                                    >
                                                        <div style={{
                                                            width: '20px',
                                                            height: '20px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0
                                                        }}>
                                                            <ExtensionFlagImage
                                                                iso2={parsed.iso2}
                                                                size="20px"
                                                                style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    display: 'block',
                                                                    objectFit: 'contain'
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-sm font-medium" style={{ color: 'var(--color-text, #111827)' }}>
                                                                {parsed.name}
                                                            </div>
                                                            <div className="text-xs" style={{ color: 'var(--color-text-muted, #6b7280)' }}>
                                                                +{parsed.dialCode}
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            })
                                        ) : (
                                            <div className="px-3 py-4 text-center text-sm" style={{ color: 'var(--color-text-muted, #6b7280)' }}>
                                                {t('startChatWithPhoneNumber.noCountriesFound')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Full Number Display */}
                        {inputValue && (
                            <div className="flex items-center gap-2 text-xs">
                                <span style={{ color: 'var(--color-text-muted, #6b7280)' }}>{t('startChatWithPhoneNumber.fullNumber')}</span>
                                <span className="font-medium" style={{ color: 'var(--color-text, #111827)' }}>{inputValue}</span>
                                {country && inputValue && inputValue.startsWith(country.dialCode) && (() => {
                                    const countryData = customCountries.find((c: CountryData) => {
                                        const parsed = parseCountry(c);
                                        return parsed.iso2 === country.iso2;
                                    });
                                    const parsedCountry = countryData ? parseCountry(countryData) : null;
                                    return parsedCountry ? (
                                        <span style={{ color: 'var(--color-text-muted, #6b7280)' }}>
                                            ({parsedCountry.name})
                                        </span>
                                    ) : null;
                                })()}
                            </div>
                        )}
                    </div>
                    ) : (
                    /* Chat ID Input */
                    <div className="space-y-3">
                        <label htmlFor="chatId" className="text-sm font-medium leading-none" style={{ color: 'var(--color-text, #111827)' }}>
                            {t('startChatWithPhoneNumber.chatId', 'Chat ID')}
                        </label>
                        <input
                            id="chatId"
                            type="text"
                            placeholder="1234567890@c.us"
                            value={chatIdInput}
                            onChange={handleChatIdChange}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            className={cn(
                                "flex h-11 w-full rounded-lg border bg-background pl-3 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all font-mono"
                            )}
                            style={{
                                borderColor: 'var(--color-border, #e5e7eb)',
                                backgroundColor: 'var(--color-surface, #ffffff)',
                                color: 'var(--color-text, #111827)',
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                                e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = 'var(--color-border, #e5e7eb)';
                                e.target.style.boxShadow = '';
                            }}
                        />
                        <div className="text-xs space-y-1" style={{ color: 'var(--color-text-muted, #6b7280)' }}>
                            <p>{t('startChatWithPhoneNumber.chatIdExamples', 'Examples:')}</p>
                            <p className="font-mono">1234567890@c.us</p>
                            <p className="font-mono">1234567890-1234567890@g.us</p>
                            <p className="font-mono">1234567890@newsletter</p>
                        </div>
                    </div>
                    )}
                    
                    {invalidWANumber && (
                        <div className="w-full p-3 rounded-md" style={{ 
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            color: 'rgb(239, 68, 68)'
                        }}>
                            <p className="text-sm font-medium">{t('startChatWithPhoneNumber.notValidWhatsAppNumber')}</p>
                        </div>
                    )}
                    
                    {/* Start Chat Button */}
                    <DialogFooter className="w-full">
                        <button
                            onClick={handleStartChat}
                            disabled={isStartChatDisabled}
                            className="w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-11 px-4 py-2 shadow-sm"
                            style={{
                                backgroundColor: 'var(--color-primary, #3b82f6)',
                                color: 'var(--color-primary-contrast, #ffffff)',
                            }}
                        >
                            <MessageCircle className="h-4 w-4" />
                            {t('startChatWithPhoneNumber.startChat')}
                        </button>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default StartChatWithPhoneNumber;
